import { useCallback, useMemo, useRef } from 'react';
import { getAnnotationCategory } from '../../utils/annotationCategories';
import type { TranscriptMark } from '../../utils/transcriptMarks';
import type { TranscriptMarkingProps } from '../Transcript/transcriptMarking';
import { MarkNotify, TranscriptMarkStore, useTranscriptMarks } from '../Transcript/useTranscriptMarks';
import { StudioAnnotation, newAnnotationId } from './annotationModel';

/**
 * Marking the transcript in the single-video studio: a mark is one more note
 * in the list, saved with the rest by autosave, so Undo just puts the list
 * back the way it was.
 */

interface Options {
  annotations: StudioAnnotation[];
  /** The notes have loaded (saving now can't erase anything). */
  editable: boolean;
  loadFailed: boolean;
  commit: (update: (previous: StudioAnnotation[]) => StudioAnnotation[]) => void;
  selectedId: string | null;
  setSelectedId: React.Dispatch<React.SetStateAction<string | null>>;
  notify: MarkNotify;
  seek: (seconds: number) => void;
  onEdit: (id: string) => void;
}

export function useStudioTranscriptMarks({
  annotations,
  editable,
  loadFailed,
  commit,
  selectedId,
  setSelectedId,
  notify,
  seek,
  onEdit,
}: Options): TranscriptMarkingProps {
  const latest = useRef(annotations);
  latest.current = annotations;

  const store = useMemo<TranscriptMarkStore>(() => ({
    add: async (note) => {
      const created: StudioAnnotation = {
        id: newAnnotationId(),
        time: note.time,
        text: note.text,
        category: note.category ?? 'general',
        color: note.color ?? getAnnotationCategory(note.category).color,
        ...(typeof note.endTime === 'number' && note.endTime > note.time ? { endTime: note.endTime } : {}),
      };
      commit((previous) => [...previous, created]);
      setSelectedId(created.id);
      return created.id;
    },
    remove: async (id) => {
      const target = latest.current.find((annotation) => annotation.id === id);
      if (!target) return null;
      commit((previous) => previous.filter((annotation) => annotation.id !== id));
      setSelectedId((current) => (current === id ? null : current));
      return async () => {
        commit((previous) => (previous.some((annotation) => annotation.id === target.id) ? previous : [...previous, target]));
      };
    },
  }), [commit, setSelectedId]);

  const onSelect = useCallback((mark: TranscriptMark) => {
    setSelectedId(mark.id);
    seek(mark.start);
  }, [seek, setSelectedId]);
  const onClearSelection = useCallback(() => setSelectedId(null), [setSelectedId]);
  const onEditMark = useCallback((mark: TranscriptMark) => onEdit(mark.id), [onEdit]);

  let disabledReason: string | null = null;
  if (loadFailed) disabledReason = 'Reload your notes (above) before marking words, so nothing is lost.';
  else if (!editable) disabledReason = 'Words can be marked once your notes have loaded.';

  return useTranscriptMarks({
    notes: annotations,
    readOnly: false,
    disabledReason,
    selectedId,
    store,
    notify,
    onSelect,
    onClearSelection,
    onEdit: onEditMark,
  });
}
